import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Phone } from "lucide-react";
import { useContent } from "../content-context";
import { trackEvent } from "../analytics/ga4";
import { Eyebrow, Reveal } from "./Reveal";
import API from "../api";

const WHATSAPP_ICON = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const initial = {
  full_name: "",
  email: "",
  phone: "",
  arrival_date: "",
  departure_date: "",
  guests: "",
  occasion: "",
  message: "",
};

const labelCls = "block text-xs font-semibold uppercase tracking-[0.18em] text-navy";
const inputCls = "mt-2 w-full rounded-md border border-navy/25 bg-white px-4 py-3 text-base font-normal text-navy outline-none transition-all duration-300 focus:border-gold focus:ring-2 focus:ring-gold/25 placeholder:text-navy/40";
const errCls = "mt-1.5 text-xs font-medium text-red-600";

export default function Contact() {
  const { contact, site } = useContent();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const errs = {};
    if (!form.full_name.trim()) errs.full_name = "Please tell us your name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = "Please enter a valid email address";
    if (form.phone.trim().length < 5) errs.phone = "Please enter a contact number";
    if (!form.arrival_date) errs.arrival_date = "Preferred arrival date is required";
    if (!form.departure_date) errs.departure_date = "Preferred departure date is required";
    if (form.arrival_date && form.departure_date && form.departure_date <= form.arrival_date)
      errs.departure_date = "Departure must be after arrival";
    if (!form.guests || form.guests < 1) errs.guests = "How many guests are coming?";
    if (!form.occasion) errs.occasion = "Please choose an occasion";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, guests: Number(form.guests) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to send");
      toast.success("Enquiry received — we will be in touch very soon.", {
        description: "A confirmation has been sent to the island team.",
      });
      trackEvent("generate_lead", {
        guests: Number(form.guests) || undefined,
        occasion: form.occasion || undefined,
      });
      setForm(initial);
      setErrors({});
    } catch (err) {
      toast.error("Your enquiry could not be sent. Please WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-ivory py-24 md:py-32">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-5 md:px-10 lg:grid-cols-2 lg:gap-20">
        <div>
          <Reveal>
            <Eyebrow>{contact.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl font-medium leading-[1.05] text-navy md:text-6xl" data-testid="contact-heading">
              Begin your <em className="text-gold">island story</em>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-navy/75 md:text-base" data-testid="contact-lead">
              {contact.lead}
            </p>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-10 flex flex-col gap-5">
              <a
                href={site.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="group inline-flex items-center gap-4 self-start bg-[#25D366] px-7 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-transform duration-500 hover:scale-[1.02]"
              >
                {WHATSAPP_ICON}
                WhatsApp {site.phoneDisplay}
              </a>
              <a href={`mailto:${site.email}`} data-testid="contact-email-link" className="group flex items-center gap-4 py-1 text-sm font-light text-navy/80 transition-colors hover:text-gold">
                <span className="flex h-10 w-10 items-center justify-center border border-navy/15 text-gold transition-colors group-hover:border-gold">
                  <Mail className="h-4 w-4" />
                </span>
                {site.email}
              </a>
              <a href={site.phoneHref} data-testid="contact-phone-link" className="group flex items-center gap-4 py-1 text-sm font-light text-navy/80 transition-colors hover:text-gold">
                <span className="flex h-10 w-10 items-center justify-center border border-navy/15 text-gold transition-colors group-hover:border-gold">
                  <Phone className="h-4 w-4" />
                </span>
                {site.phoneDisplay}
              </a>
              <p className="flex items-start gap-4 py-1 text-sm font-light text-navy/80">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-navy/15 text-gold">
                  <MapPin className="h-4 w-4" />
                </span>
                <span data-testid="contact-address">{site.address}</span>
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <form
            onSubmit={submit}
            noValidate
            data-testid="enquiry-form"
            className="border border-navy/10 bg-white p-7 shadow-[0_8px_32px_rgba(10,25,47,0.08)] md:p-10"
          >
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="enquiry-name">Full Name *</label>
                <input id="enquiry-name" data-testid="enquiry-name-input" className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Your name" />
                {errors.full_name && <p className={errCls} data-testid="enquiry-name-error">{errors.full_name}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="enquiry-email">Email Address *</label>
                <input id="enquiry-email" type="email" data-testid="enquiry-email-input" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                {errors.email && <p className={errCls} data-testid="enquiry-email-error">{errors.email}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="enquiry-phone">Telephone Number *</label>
                <input id="enquiry-phone" type="tel" data-testid="enquiry-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+353 ..." />
                {errors.phone && <p className={errCls} data-testid="enquiry-phone-error">{errors.phone}</p>}
              </div>
              <div>
                <label className={labelCls} htmlFor="enquiry-arrival">Preferred Arrival *</label>
                <input id="enquiry-arrival" type="date" data-testid="enquiry-arrival-input" className={inputCls} value={form.arrival_date} onChange={set("arrival_date")} />
                {errors.arrival_date && <p className={errCls} data-testid="enquiry-arrival-error">{errors.arrival_date}</p>}
              </div>
              <div>
                <label className={labelCls} htmlFor="enquiry-departure">Preferred Departure *</label>
                <input id="enquiry-departure" type="date" data-testid="enquiry-departure-input" className={inputCls} value={form.departure_date} onChange={set("departure_date")} />
                {errors.departure_date && <p className={errCls} data-testid="enquiry-departure-error">{errors.departure_date}</p>}
              </div>
              <div>
                <label className={labelCls} htmlFor="enquiry-guests">Number of Guests *</label>
                <input id="enquiry-guests" type="number" min="1" max="22" data-testid="enquiry-guests-input" className={inputCls} value={form.guests} onChange={set("guests")} placeholder="Up to 22" />
                {errors.guests && <p className={errCls} data-testid="enquiry-guests-error">{errors.guests}</p>}
              </div>
              <div>
                <label className={labelCls} htmlFor="enquiry-occasion">Type of Celebration *</label>
                <div className="relative">
                  <select id="enquiry-occasion" data-testid="enquiry-occasion-select" className={inputCls} value={form.occasion} onChange={set("occasion")}>
                    <option value="">Choose an occasion…</option>
                    {contact.occasions.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gold" aria-hidden="true">▾</span>
                </div>
                {errors.occasion && <p className={errCls} data-testid="enquiry-occasion-error">{errors.occasion}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="enquiry-message">Additional Information</label>
                <textarea id="enquiry-message" data-testid="enquiry-message-input" rows="4" className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about your group, dates or anything you would like arranged…" />
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-button"
              className="mt-8 w-full bg-navy py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-navy disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
